import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import classNames from 'classnames';
import { AnchorButton, ButtonAsLink } from '@cluey/cluey-components';

import { SessionType } from '../../../types/hubPropTypes';
import { DesktopOnly } from '../../DesktopOnly/DesktopOnly';
import { formatSession } from '../../../util/helpers';
import SessionPaymentInfo from './SessionPaymentInfo';
import { saveSelectedSession } from '../../../actions/hub/hubContactPageActions';
import { PATH_HUB_CR_REQUEST_RECORDING, PATH_NAME_REPORT } from '../../../util/pagePath';
import {
  STUDENT_PROGRESS_TAB_VIEWS,
  STUDENT_PROGRESS_URL_PARAM_TAB,
  DATE_FORMAT_TYPE_13,
  TTBM_CUSTOMER_NUMBER,
} from '../../../util/constants';
import { SessionDetails } from './HubSessionsListItem/SessionDetails';
import { getPkgTutorNameByEnrolmentId } from '../../../selectors/hubEnrolmentsSelector';
import courseIcon from '../../../assets/images/icon-filled-session-course.svg';
import classroomIcon from '../../../assets/images/icon-filled-session-masterclass.svg';
import tickIcon from '../../../assets/images/icon-filled-alert-tick.svg';
import clockIcon from '../../../assets/images/icon-clock--orange.svg';
import raysIcon from '../../../assets/images/icon-filled-rays.svg';

const CustomLink = ({ link, label, className }) => (
  <a
    href={link}
    rel="noopener noreferrer"
    target="_blank"
    className={classNames('lh-1 mb-2 block text-primary hover:underline', className)}
  >
    {label}
  </a>
);

CustomLink.propTypes = {
  link: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  className: PropTypes.string,
};

const HubCompletedSessionsListItem = ({ session, timezone, saveSelectedSessionToState }) => {
  const {
    student,
    subject,
    uniqueEnrolmentId,
    sessionReportId,
    tutor,
    recordingLink,
    tutorNumber,
    startTime,
    endTime,
    classroomPractice,
    type,
  } = session;
  const getFormattedTime = (time) => moment(time).tz(timezone).format('h:mma');
  const timeFrame = `${getFormattedTime(startTime)} - ${getFormattedTime(endTime)}`;
  const packageTutor = useSelector((state) => getPkgTutorNameByEnrolmentId(state, uniqueEnrolmentId));
  const displayTutor = tutorNumber === TTBM_CUSTOMER_NUMBER ? packageTutor : tutor;
  const isClassroomSession = type === 'Classroom';
  const isClassroomPracticeDone =
    classroomPractice && classroomPractice.questionsCompleted === classroomPractice.questionsTotal;

  const history = useHistory();
  const onClickHandler = () => {
    saveSelectedSessionToState({
      session,
    });

    history.push(PATH_HUB_CR_REQUEST_RECORDING);
  };

  const { date } = formatSession({
    session,
    timezone,
    sessionDateFormat: DATE_FORMAT_TYPE_13,
    useSemiColon: false,
  });

  let sessionType: string = '';
  let sessionIcon: string = classroomIcon;
  switch (type) {
    case 'Classroom':
      sessionType = 'Masterclass';
      break;
    case 'Group':
      sessionType = 'Group';
      break;
    case 'Program':
      sessionType = 'Program';
      break;
    default:
      sessionType = '1-on-1';
      sessionIcon = courseIcon;
      break;
  }

  const courseOptions = (className?: string) => (
    <div className={className}>
      <CustomLink
        link={`${PATH_NAME_REPORT}?token=${sessionReportId}&${STUDENT_PROGRESS_URL_PARAM_TAB}=${STUDENT_PROGRESS_TAB_VIEWS.SESSION_REPORT}`}
        label="View learning activities"
        className="text-left lg:text-right"
      />
      {recordingLink && recordingLink != null ? (
        <CustomLink link={recordingLink} label="Watch session recording" />
      ) : (
        <ButtonAsLink
          size="base"
          className="ls-normal lh-1-14 border-radius-0 block w-full p-0 text-left font-body text-primary hover:underline md:text-right"
          onClick={onClickHandler}
        >
          Request session recording
        </ButtonAsLink>
      )}
    </div>
  );

  const classroomOptions = (className?: string) => (
    <div className={classNames('flex flex-col items-start gap-2 lg:items-end', className)}>
      <AnchorButton
        size="sm"
        className="mb-1 hidden min-w-[80px] !px-6 normal-case lg:inline-flex lg:items-center lg:justify-center"
        href={classroomPractice.practiceSessionLink}
      >
        {isClassroomPracticeDone ? 'View practice questions' : 'Practice what I learnt'}
      </AnchorButton>
      {!isClassroomPracticeDone && (
        <div className="flex flex-row items-center gap-x-2">
          <img
            src={classroomPractice.questionsCompleted === 0 ? raysIcon : clockIcon}
            className="h-5 w-5"
            alt="questions icon"
          />
          <span>
            {classroomPractice.questionsCompleted} of {classroomPractice.questionsTotal} questions completed
          </span>
        </div>
      )}
      {isClassroomPracticeDone && (
        <div className="flex flex-row items-center gap-x-2">
          <img src={tickIcon} className="h-5 w-5" alt="tick icon" />
          <span>All questions completed</span>
        </div>
      )}
      {/* {recordingLink && recordingLink != null ? (
        <CustomLink link={recordingLink} label="Watch recording" />
      ) : (
        <ButtonAsLink
          size="base"
          className="ls-normal lh-1-14 border-radius-0 block w-full p-0 text-left font-body text-primary hover:underline md:text-right"
          onClick={onClickHandler}
        >
          Request session recording
        </ButtonAsLink>
      )} */}
    </div>
  );

  return (
    <div
      data-testid="session-list-item"
      className="relative flex w-full flex-col items-start gap-1 rounded-lg border-2 border-grey-2 px-5 py-3 shadow-md xl:flex"
    >
      <div className="absolute -left-[0.5px] -top-[0.5px] rounded-br-lg rounded-tl-lg bg-slate-4 text-base font-bold">
        <div className="flex flex-row items-center gap-x-2 px-3 py-2">
          <img src={sessionIcon} className="h-5 w-5" alt="session icon" />
          <span className="text-white">{sessionType}</span>
        </div>
      </div>
      <div className="flex w-full flex-col lg:flex-row">
        <div className="mt-9 flex w-full grow basis-2/3 flex-col items-start justify-between gap-2">
          <div className="flex flex-col gap-1">
            <SessionDetails student={student} subject={subject} tutor={displayTutor} />
            <span className="text-xs font-bold uppercase">{date}</span>
            <div className="text-base font-normal">{timeFrame}</div>
          </div>
          <DesktopOnly className={isClassroomSession ? 'flex' : 'hidden'} />
          {isClassroomSession && classroomOptions('flex lg:hidden')}
          {!isClassroomSession && courseOptions('flex flex-col lg:hidden')}
          {!isClassroomSession && <SessionPaymentInfo session={session} />}
        </div>
        <div className="hidden w-full basis-1/3 flex-col items-end gap-1 lg:flex">
          {isClassroomSession && classroomOptions('justify-items-end')}
          {!isClassroomSession && courseOptions()}
        </div>
      </div>
    </div>
  );
};

HubCompletedSessionsListItem.propTypes = {
  session: SessionType.isRequired,
  timezone: PropTypes.string.isRequired,
  saveSelectedSessionToState: PropTypes.func.isRequired,
};

export default connect(null, (dispatch) => {
  return {
    saveSelectedSessionToState: bindActionCreators(saveSelectedSession, dispatch),
  };
})(HubCompletedSessionsListItem);

export { HubCompletedSessionsListItem as StatelessHubCompletedSessionsListItem };
